import { useState } from 'react';

export default function BeckhamCalculator() {
  const [salario, setSalario] = useState<number>(50000);
  const [mostrarResultado, setMostrarResultado] = useState(false);

  const calcularIRPFProgresivo = (salarioAnual: number): number => {
    let impuesto = 0;
    
    if (salarioAnual <= 12450) {
      impuesto = salarioAnual * 0.19;
    } else if (salarioAnual <= 20200) {
      impuesto = 12450 * 0.19 + (salarioAnual - 12450) * 0.24;
    } else if (salarioAnual <= 35200) {
      impuesto = 12450 * 0.19 + (20200 - 12450) * 0.24 + (salarioAnual - 20200) * 0.30;
    } else if (salarioAnual <= 60000) {
      impuesto = 12450 * 0.19 + (20200 - 12450) * 0.24 + (35200 - 20200) * 0.30 + (salarioAnual - 35200) * 0.37;
    } else if (salarioAnual <= 300000) {
      impuesto = 12450 * 0.19 + (20200 - 12450) * 0.24 + (35200 - 20200) * 0.30 + (60000 - 35200) * 0.37 + (salarioAnual - 60000) * 0.45;
    } else {
      impuesto = 12450 * 0.19 + (20200 - 12450) * 0.24 + (35200 - 20200) * 0.30 + (60000 - 35200) * 0.37 + (300000 - 60000) * 0.45 + (salarioAnual - 300000) * 0.47;
    }
    
    return impuesto;
  };

  const calcularBeckham = (salarioAnual: number): number => {
    return salarioAnual * 0.24;
  };

  const handleCalcular = () => {
    setMostrarResultado(true);
  };

  const irpfProgresivo = calcularIRPFProgresivo(salario);
  const irpfBeckham = calcularBeckham(salario);
  const ahorro = irpfProgresivo - irpfBeckham;
  const porcentajeAhorro = ((ahorro / irpfProgresivo) * 100).toFixed(1);
  const salarioNetoProgresivo = salario - irpfProgresivo;
  const salarioNetoBeckham = salario - irpfBeckham;

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-6">
                <i className="ri-calculator-fill text-4xl text-white"></i>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Calculadora Ley Beckham
              </h2>
              <p className="text-xl text-gray-700">
                Descubre cuánto puedes ahorrar con el régimen fiscal especial
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12">
              <div className="mb-8">
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  ¿Cuál es tu salario bruto anual?
                </label>
                <div className="relative">
                  <input
                    type="range"
                    min="20000"
                    max="150000"
                    step="1000"
                    value={salario}
                    onChange={(e) => setSalario(Number(e.target.value))}
                    className="w-full h-3 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-sm text-gray-600 mt-2">
                    <span>20.000€</span>
                    <span>150.000€</span>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <div className="inline-block bg-blue-600 text-white px-8 py-4 rounded-xl">
                    <span className="text-5xl font-bold">
                      {salario.toLocaleString('es-ES')}€
                    </span>
                    <span className="text-xl ml-2">/ año</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCalcular}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-5 rounded-xl font-bold text-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Calcular Ahorro
              </button>

              {mostrarResultado && (
                <div className="mt-10 space-y-6 animate-fadeIn">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-full">
                          <i className="ri-arrow-down-line text-2xl text-white"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">
                          IRPF Progresivo
                        </h3>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700">Impuestos:</span>
                          <span className="text-2xl font-bold text-red-600">
                            {irpfProgresivo.toLocaleString('es-ES', { maximumFractionDigits: 0 })}€
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-red-200">
                          <span className="text-gray-700 font-semibold">Salario neto:</span>
                          <span className="text-xl font-bold text-gray-900">
                            {salarioNetoProgresivo.toLocaleString('es-ES', { maximumFractionDigits: 0 })}€
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-full">
                          <i className="ri-arrow-up-line text-2xl text-white"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">
                          Ley Beckham (24%)
                        </h3>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700">Impuestos:</span>
                          <span className="text-2xl font-bold text-green-600">
                            {irpfBeckham.toLocaleString('es-ES', { maximumFractionDigits: 0 })}€
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-green-200">
                          <span className="text-gray-700 font-semibold">Salario neto:</span>
                          <span className="text-xl font-bold text-gray-900">
                            {salarioNetoBeckham.toLocaleString('es-ES', { maximumFractionDigits: 0 })}€
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4">
                      <i className="ri-money-dollar-circle-fill text-4xl text-orange-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Tu Ahorro Anual
                    </h3>
                    <div className="text-6xl font-bold text-white mb-2">
                      {ahorro.toLocaleString('es-ES', { maximumFractionDigits: 0 })}€
                    </div>
                    <p className="text-xl text-gray-900 font-semibold">
                      Ahorras un {porcentajeAhorro}% en impuestos
                    </p>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-information-fill text-3xl text-blue-600"></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2 text-lg">
                          ¿Cómo funciona la Ley Beckham?
                        </h4>
                        <p className="text-gray-700 leading-relaxed">
                          La Ley Beckham te permite tributar como no residente fiscal con un tipo fijo del 24% sobre tus ingresos laborales, en lugar del IRPF progresivo que puede llegar hasta el 47%. Es ideal para empleados que se trasladan a España con el visado de nómada digital.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="text-center pt-6">
                    <a
                      href="#consulta"
                      className="inline-block bg-blue-600 text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                    >
                      Agendar consulta
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-question-line text-2xl text-blue-600"></i>
                </div>
                Requisitos para la Ley Beckham
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                  </div>
                  <span className="text-gray-700">
                    No haber sido residente fiscal en España en los últimos 5 años
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                  </div>
                  <span className="text-gray-700">
                    Trasladarte a España por motivos laborales como empleado
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-2xl text-green-600"></i>
                  </div>
                  <span className="text-gray-700">
                    Solicitar el régimen en los 6 meses siguientes al inicio de la actividad
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Asesoría profesional section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl shadow-xl p-8 text-white">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mb-6">
                    <i className="ri-shield-check-fill text-4xl text-white"></i>
                  </div>
                  <h3 className="text-3xl font-bold mb-4">
                    Asesoría profesional para evitar rechazos y perder tiempo
                  </h3>
                  <p className="text-xl mb-6 leading-relaxed">
                    El visado de nómada digital es una oportunidad extraordinaria, pero su tramitación exige documentación precisa, legalizaciones, contratos válidos, traducciones juradas y argumentación correcta.
                  </p>
                  <p className="text-lg mb-8">
                    Cualquier error puede derivar en retrasos, rechazo de la solicitud y pérdida de tiempo y dinero. Nuestro equipo especializado te acompaña en cada paso para garantizar el éxito.
                  </p>
                  <a 
                    href="#consulta" 
                    className="inline-block bg-white text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    Solicitar Asesoría
                  </a>
                </div>
                
                <div className="order-first lg:order-last">
                  <img 
                    src="https://static.readdy.ai/image/97c320206ed08475f986f4f05d477848/d902a1ca617f57a4f99b2ea7c5fd79ec.png" 
                    alt="Asesoría profesional para visado de nómada digital"
                    className="w-full h-auto rounded-xl shadow-2xl object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
